import { KeyboardEvent, memo, useState } from 'react';

import {
  Box,
  BoxProps,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  rem,
  Text,
} from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';
import Linkify from '@vimeo/ui/core/Linkify';

interface Props {
  color: BoxProps['color'];
  maxLength?: number;
  modalHeader: string;
  text: string;
  showLinksInDescription?: boolean;
}

export const EllipsisTextWithShowMore = memo(
  ({ color, maxLength = 200, modalHeader, text, showLinksInDescription }: Props) => {
    const isTextOverflowing = text.length > maxLength;
    const ellipsisText = isTextOverflowing ? `${text.substring(0, maxLength)}… ` : text;
    const [showModal, setShowModal] = useState(false);

    return (
      <>
        {showLinksInDescription ? <Linkify text={ellipsisText} /> : ellipsisText}
        {isTextOverflowing && (
          <Box
            color={color}
            as="span"
            cursor="pointer"
            onClick={() => setShowModal(true)}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setShowModal(true);
              }
            }}
            tabIndex={0}
          >
            {translate('more')}
          </Box>
        )}
        {showModal && (
          <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
            <ModalOverlay />
            <ModalContent maxW={rem(480)} minH={rem(300)} overflow="hidden">
              <ModalHeader pr={rem(72)}>{modalHeader}</ModalHeader>
              <ModalCloseButton />
              <ModalBody mt="md" pt={0} pb="lg">
                <Text variant="body-md">
                  {showLinksInDescription ? <Linkify text={text} /> : text}
                </Text>
              </ModalBody>
            </ModalContent>
          </Modal>
        )}
      </>
    );
  },
);
